// frontendBSCalculations.ts
// Frontend Balance Sheet calculation service that mirrors UnifiedDetailedBalanceSheet component logic
import { AccountEntry, CategoryTemplate, ViewType, AccountType } from '../../types';
import { compareMonthYear } from './calculations';

// Interface matching the backend BSLineItem structure
export interface BSLineItem {
  type: 'section_header' | 'category_header' | 'account' | 'category_total' | 'section_total' | 'major_total' | 'calculated_total' | 'balance_check';
  name: string;
  values?: number[];
  level: number;
  section_id?: string;
  category_value?: string;
  account_number?: string;
  category?: string;
  is_check?: boolean;
}

export interface BSDetailData {
  periods: string[];
  line_items: BSLineItem[];
}

export interface BalanceSheetSection {
  id: string;
  title: string;
  types: AccountType[];
  categories?: string[];
}

export class FrontendBSCalculationService {
  /**
   * Build Balance Sheet data using frontend calculations that mirror the component's logic
   */
  static buildBSData(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    viewType: ViewType,
    selectedProject: any
  ): BSDetailData {
    if (!accounts.length || !activeTemplate) {
      return { periods: [], line_items: [] };
    }

    // Get all available months from accounts
    const allMonths = [...new Set(accounts.map(acc => acc.month))].sort(compareMonthYear);

    // Always generate monthly data - let the component handle view transformations
    const periods = allMonths;

    // Define Balance Sheet sections (matching component logic)
    const sections: BalanceSheetSection[] = [
      {
        id: 'current-assets',
        title: 'Current Assets',
        types: ['current-asset', 'contra-current-asset']
      },
      {
        id: 'non-current-assets',
        title: 'Non-current Assets',
        types: ['non-current-asset', 'contra-non-current-asset']
      },
      {
        id: 'current-liabilities',
        title: 'Current Liabilities',
        types: ['current-liability', 'contra-current-liability']
      },
      {
        id: 'non-current-liabilities',
        title: 'Non-current Liabilities',
        types: ['non-current-liability', 'contra-non-current-liability']
      },
      {
        id: 'equity',
        title: 'Equity',
        types: ['equity', 'contra-equity']
      }
    ];

    const line_items: BSLineItem[] = [];

    // Build line items for each section
    sections.forEach(section => {
      const sectionCategories = this.getSectionCategories(activeTemplate, section.types);
      
      if (sectionCategories.length === 0) return;

      // Section header
      line_items.push({
        type: 'section_header',
        name: section.title,
        section_id: section.id,
        level: 1
      });

      // Categories and accounts within section
      sectionCategories.forEach(categoryValue => {
        const category = activeTemplate.categories.find(cat => cat.value === categoryValue);
        if (!category) return;

        // Category header with values (point-in-time balances)
        const categoryValues = periods.map(period =>
          this.calculateCategoryBalance(accounts, categoryValue, period, category.type)
        );

        // Only add category if it has non-zero values
        if (categoryValues.some(val => Math.abs(val) > 0.01)) {
          line_items.push({
            type: 'category_header',
            name: category.label,
            category_value: categoryValue,
            category: categoryValue,
            values: categoryValues,
            level: 2
          });
        } else {
          // Skip this category entirely if all values are zero
          return;
        }

        // Individual accounts
        const accountNumbers = this.getAccountDisplayOrder(accounts, categoryValue);
        accountNumbers.forEach(accountNumber => {
          const accountName = accounts.find(a => a.accountNumber === accountNumber)?.name || '';
          const accountValues = periods.map(period => {
            return this.calculateAccountBalance(accounts, accountNumber, categoryValue, period, category.type);
          });

          if (accountValues.some(val => Math.abs(val) > 0.01)) {
            line_items.push({
              type: 'account',
              name: accountName,
              account_number: accountNumber,
              category: categoryValue,
              values: accountValues,
              level: 3
            });
          }
        });

        // Category total
        line_items.push({
          type: 'category_total',
          name: `Total ${category.label}`,
          category: categoryValue,
          values: categoryValues,
          level: 2
        });
      });

      // Section total (skip for Equity since we handle it specially with Net Income)
      if (section.id !== 'equity') {
        const sectionValues = periods.map(period =>
          this.calculateSectionTotal(accounts, activeTemplate, section.types, period)
        );

        if (sectionValues.some(val => Math.abs(val) > 0.01)) {
          line_items.push({
            type: 'section_total',
            name: `Total ${section.title}`,
            values: sectionValues,
            level: 1
          });
        }
      }

      // Add major totals after specific sections
      if (section.id === 'non-current-assets') {
        // Add Total Assets after all asset sections
        const totalAssetsValues = periods.map(period => {
          const currentAssets = this.calculateSectionTotal(accounts, activeTemplate, 
            ['current-asset', 'contra-current-asset'], period);
          const nonCurrentAssets = this.calculateSectionTotal(accounts, activeTemplate, 
            ['non-current-asset', 'contra-non-current-asset'], period);
          return currentAssets + nonCurrentAssets;
        });

        line_items.push({
          type: 'major_total',
          name: 'Total Assets',
          values: totalAssetsValues,
          level: 0
        });
      }

      if (section.id === 'non-current-liabilities') {
        // Add Total Liabilities after all liability sections
        const totalLiabilitiesValues = periods.map(period => {
          const currentLiabilities = this.calculateSectionTotal(accounts, activeTemplate, 
            ['current-liability', 'contra-current-liability'], period);
          const nonCurrentLiabilities = this.calculateSectionTotal(accounts, activeTemplate, 
            ['non-current-liability', 'contra-non-current-liability'], period);
          return currentLiabilities + nonCurrentLiabilities;
        });

        line_items.push({
          type: 'major_total',
          name: 'Total Liabilities',
          values: totalLiabilitiesValues,
          level: 0
        });
      }

      if (section.id === 'equity') {
        // Add Net Income first
        const netIncomeValues = periods.map(period => 
          this.calculateNetIncome(accounts, activeTemplate, period)
        );

        line_items.push({
          type: 'calculated_total',
          name: 'Current Period Net Income',
          values: netIncomeValues,
          level: 1
        });

        // Then Total Equity (including Net Income)
        const totalEquityValues = periods.map(period => {
          const equityBalance = this.calculateSectionTotal(accounts, activeTemplate, 
            ['equity', 'contra-equity'], period);
          const netIncome = this.calculateNetIncome(accounts, activeTemplate, period);
          return equityBalance + netIncome;
        });

        line_items.push({
          type: 'major_total',
          name: 'Total Equity',
          values: totalEquityValues,
          level: 0
        });

        // Total Liabilities and Equity
        const totalLiabilitiesAndEquityValues = periods.map((period, index) => {
          const totalLiabilities = this.calculateSectionTotal(accounts, activeTemplate, 
            ['current-liability', 'contra-current-liability', 'non-current-liability', 'contra-non-current-liability'], period);
          const totalEquity = totalEquityValues[index] || 0;
          return totalLiabilities + totalEquity;
        });

        line_items.push({
          type: 'major_total',
          name: 'Total Liabilities and Equity',
          values: totalLiabilitiesAndEquityValues,
          level: 0
        });

        // Balance check
        const balanceCheckValues = periods.map(period => {
          const totalAssets = this.calculateSectionTotal(accounts, activeTemplate, 
            ['current-asset', 'contra-current-asset', 'non-current-asset', 'contra-non-current-asset'], period);
          
          const totalLiabilities = this.calculateSectionTotal(accounts, activeTemplate, 
            ['current-liability', 'contra-current-liability', 'non-current-liability', 'contra-non-current-liability'], period);
          
          const totalEquity = this.calculateSectionTotal(accounts, activeTemplate, 
            ['equity', 'contra-equity'], period) + this.calculateNetIncome(accounts, activeTemplate, period);
          
          return totalAssets - (totalLiabilities + totalEquity);
        });

        line_items.push({
          type: 'balance_check',
          name: 'Check (Assets - L&E)',
          values: balanceCheckValues,
          level: 0,
          is_check: true
        });
      }
    });

    return {
      periods,
      line_items
    };
  }

  /**
   * Get section categories based on account types
   */
  private static getSectionCategories(template: CategoryTemplate, types: AccountType[]): string[] {
    return template.categories
      .filter(cat => types.includes(cat.type))
      .map(cat => cat.value);
  }

  /**
   * Calculate category balance for a specific period (point-in-time)
   */
  private static calculateCategoryBalance(
    accounts: AccountEntry[],
    categoryValue: string,
    period: string,
    accountType: AccountType
  ): number {
    const categoryAccounts = accounts.filter(
      acc => acc.category === categoryValue && acc.month === period
    );

    // Balance Sheet uses point-in-time balances, not cumulative flows
    const isAsset = ['current-asset', 'non-current-asset'].includes(accountType);
    const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(accountType);
    
    return categoryAccounts.reduce((total, account) => {
      return total + ((isAsset || isContraAsset) 
        ? account.debit - account.credit 
        : account.credit - account.debit);
    }, 0);
  }

  /**
   * Calculate individual account balance for a specific period
   */
  private static calculateAccountBalance(
    accounts: AccountEntry[],
    accountNumber: string,
    categoryValue: string,
    period: string,
    accountType: AccountType
  ): number {
    const accountEntries = accounts.filter(
      acc => acc.accountNumber === accountNumber && 
             acc.category === categoryValue && 
             acc.month === period
    );

    const isAsset = ['current-asset', 'non-current-asset'].includes(accountType);
    const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(accountType);

    return accountEntries.reduce((total, account) => {
      return total + ((isAsset || isContraAsset) 
        ? account.debit - account.credit 
        : account.credit - account.debit);
    }, 0);
  }

  /**
   * Calculate section total for a specific period
   */
  private static calculateSectionTotal(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    types: AccountType[],
    period: string
  ): number {
    const categories = this.getSectionCategories(activeTemplate, types);
    
    return categories.reduce((total, categoryValue) => {
      const category = activeTemplate.categories.find(cat => cat.value === categoryValue);
      if (!category) return total;
      
      return total + this.calculateCategoryBalance(accounts, categoryValue, period, category.type);
    }, 0);
  }

  /**
   * Calculate net income for a specific period (needed for equity section)
   */
  private static calculateNetIncome(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    period: string
  ): number {
    const monthAccounts = accounts.filter(account => account.month === period);
    
    // Get revenue categories
    const revenueCategories = activeTemplate.categories
      .filter(cat => cat.type === 'revenue')
      .map(cat => cat.value);
    
    // Get contra-revenue categories
    const contraRevenueCategories = activeTemplate.categories
      .filter(cat => cat.type === 'contra-revenue')
      .map(cat => cat.value);
    
    // Get expense categories (all expense types)
    const expenseCategories = activeTemplate.categories
      .filter(cat => ['cogs', 'contra-cogs', 'operating-expense', 'interest-expense', 
                      'income-tax-expense', 'other-expense'].includes(cat.type))
      .map(cat => cat.value);
    
    // Calculate revenue
    const revenue = monthAccounts
      .filter(account => revenueCategories.includes(account.category))
      .reduce((total, account) => total + (account.credit - account.debit), 0);
    
    // Calculate contra-revenue
    const contraRevenue = monthAccounts
      .filter(account => contraRevenueCategories.includes(account.category))
      .reduce((total, account) => total + (account.debit - account.credit), 0);
    
    // Calculate expenses
    const expenses = monthAccounts
      .filter(account => expenseCategories.includes(account.category))
      .reduce((total, account) => total + (account.debit - account.credit), 0);
    
    // Net Income = Revenue - Contra Revenue - Expenses
    return revenue - contraRevenue - expenses;
  }


  /**
   * Get account display order for a category
   */
  private static getAccountDisplayOrder(accounts: AccountEntry[], categoryValue: string): string[] {
    const categoryAccounts = accounts.filter(acc => acc.category === categoryValue);
    const uniqueAccountNumbers = [...new Set(categoryAccounts.map(acc => acc.accountNumber))]
      .filter((acc): acc is string => typeof acc === 'string' && !!acc);

    return uniqueAccountNumbers.sort((a, b) => {
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
  }
}
